import type { AdminDashboardData, AdminUser, CoachingTheme } from '@/types/admin';

// Mock data service - in a real app, this would fetch from an API
export class AdminDataService {
  private static instance: AdminDataService;
  
  public static getInstance(): AdminDataService {
    if (!AdminDataService.instance) {
      AdminDataService.instance = new AdminDataService();
    }
    return AdminDataService.instance;
  }

  public getDashboardData(): AdminDashboardData {
    const users = this.getUsers();
    const activeUsers = this.getActiveUsers();
    const repeatUsers = this.getRepeatUsers();
    
    // Calculate status counts from actual data
    const statusCounts = users.reduce((acc, user) => {
      const status = user.status.toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return {
      coacheeStats: {
        thriving: statusCounts.thriving || 0,
        managing: statusCounts.managing || 0,
        struggling: statusCounts.struggling || 0,
      },
      usageStats: {
        accountsCreated: users.length,
        activeUsers: activeUsers.length,
        repeatUsers: repeatUsers.length,
        averageSessionLength: 12,
        activeUsersChange: 20,
        repeatUsersChange: 30,
        sessionLengthChange: 5,
      },
      coachingThemes: this.getCoachingThemes(),
      users: this.getUsers(),
    };
  }

  public getUsers(): AdminUser[] {
    // Get current date for calculating active users
    const today = new Date();
    const formatDate = (daysAgo: number) => {
      const date = new Date(today);
      date.setDate(date.getDate() - daysAgo);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };
    
    return [
      // Thriving users (20 total)
      { id: '1', name: 'Mei Ling Wong', sessions: 25, averageSessionLength: '25 min', lastSession: formatDate(1), status: 'Thriving' },
      { id: '2', name: 'Siti Nurhaliza', sessions: 24, averageSessionLength: '30 min', lastSession: formatDate(2), status: 'Thriving' },
      { id: '3', name: 'Zhen Li', sessions: 23, averageSessionLength: '16 min', lastSession: formatDate(3), status: 'Thriving' },
      { id: '4', name: 'Xiu Ying Lim', sessions: 22, averageSessionLength: '27 min', lastSession: formatDate(4), status: 'Thriving' },
      { id: '5', name: 'Hao Chen', sessions: 21, averageSessionLength: '29 min', lastSession: formatDate(5), status: 'Thriving' },
      { id: '6', name: 'Anjali Patel', sessions: 20, averageSessionLength: '19 min', lastSession: formatDate(6), status: 'Thriving' },
      { id: '7', name: 'Wei Zhang', sessions: 19, averageSessionLength: '24 min', lastSession: formatDate(7), status: 'Thriving' },
      { id: '8', name: 'Lakshmi Nair', sessions: 18, averageSessionLength: '26 min', lastSession: formatDate(8), status: 'Thriving' },
      { id: '9', name: 'Rahul Verma', sessions: 17, averageSessionLength: '23 min', lastSession: formatDate(9), status: 'Thriving' },
      { id: '10', name: 'Ming Zhao', sessions: 16, averageSessionLength: '28 min', lastSession: formatDate(10), status: 'Thriving' },
      { id: '11', name: 'Deepa Pillai', sessions: 15, averageSessionLength: '22 min', lastSession: formatDate(11), status: 'Thriving' },
      { id: '12', name: 'Jun Tanaka', sessions: 14, averageSessionLength: '20 min', lastSession: formatDate(12), status: 'Thriving' },
      { id: '13', name: 'Emma Liu', sessions: 13, averageSessionLength: '21 min', lastSession: formatDate(13), status: 'Thriving' },
      { id: '14', name: 'David Park', sessions: 12, averageSessionLength: '25 min', lastSession: formatDate(14), status: 'Thriving' },
      { id: '15', name: 'Sophia Kim', sessions: 11, averageSessionLength: '18 min', lastSession: formatDate(15), status: 'Thriving' },
      { id: '16', name: 'Michael Wong', sessions: 10, averageSessionLength: '27 min', lastSession: formatDate(16), status: 'Thriving' },
      { id: '17', name: 'Isabella Chen', sessions: 9, averageSessionLength: '23 min', lastSession: formatDate(17), status: 'Thriving' },
      { id: '18', name: 'James Lee', sessions: 8, averageSessionLength: '20 min', lastSession: formatDate(18), status: 'Thriving' },
      { id: '19', name: 'Olivia Tan', sessions: 7, averageSessionLength: '24 min', lastSession: formatDate(19), status: 'Thriving' },
      { id: '20', name: 'William Ng', sessions: 6, averageSessionLength: '26 min', lastSession: formatDate(20), status: 'Thriving' },
      
      // Managing users (15 total)
      { id: '21', name: 'Jia Wei Tan', sessions: 8, averageSessionLength: '18 min', lastSession: formatDate(5), status: 'Managing' },
      { id: '22', name: 'Asha Rani', sessions: 7, averageSessionLength: '21 min', lastSession: formatDate(10), status: 'Managing' },
      { id: '23', name: 'Ravi Kumar', sessions: 6, averageSessionLength: '22 min', lastSession: formatDate(15), status: 'Managing' },
      { id: '24', name: 'Arjun Singh', sessions: 5, averageSessionLength: '18 min', lastSession: formatDate(20), status: 'Managing' },
      { id: '25', name: 'Lisa Nguyen', sessions: 4, averageSessionLength: '17 min', lastSession: formatDate(25), status: 'Managing' },
      { id: '26', name: 'Kiran Desai', sessions: 4, averageSessionLength: '19 min', lastSession: formatDate(28), status: 'Managing' },
      { id: '27', name: 'Fen Liu', sessions: 3, averageSessionLength: '16 min', lastSession: formatDate(35), status: 'Managing' },
      { id: '28', name: 'Suresh Babu', sessions: 3, averageSessionLength: '15 min', lastSession: formatDate(40), status: 'Managing' },
      { id: '29', name: 'Maria Garcia', sessions: 5, averageSessionLength: '20 min', lastSession: formatDate(22), status: 'Managing' },
      { id: '30', name: 'Ahmed Hassan', sessions: 4, averageSessionLength: '19 min', lastSession: formatDate(27), status: 'Managing' },
      { id: '31', name: 'Elena Popov', sessions: 6, averageSessionLength: '21 min', lastSession: formatDate(18), status: 'Managing' },
      { id: '32', name: 'Carlos Rodriguez', sessions: 5, averageSessionLength: '17 min', lastSession: formatDate(24), status: 'Managing' },
      { id: '33', name: 'Yuki Yamamoto', sessions: 4, averageSessionLength: '16 min', lastSession: formatDate(29), status: 'Managing' },
      { id: '34', name: 'Nina Patel', sessions: 3, averageSessionLength: '18 min', lastSession: formatDate(45), status: 'Managing' },
      { id: '35', name: 'Robert Zhang', sessions: 5, averageSessionLength: '15 min', lastSession: formatDate(50), status: 'Managing' },
      
      // Struggling users (10 total)
      { id: '36', name: 'Priya Sharma', sessions: 2, averageSessionLength: '10 min', lastSession: formatDate(60), status: 'Struggling' },
      { id: '37', name: 'Kumar Sai', sessions: 2, averageSessionLength: '12 min', lastSession: formatDate(65), status: 'Struggling' },
      { id: '38', name: 'Lin Wei', sessions: 1, averageSessionLength: '8 min', lastSession: formatDate(70), status: 'Struggling' },
      { id: '39', name: 'Sarah Chen', sessions: 1, averageSessionLength: '5 min', lastSession: formatDate(75), status: 'Struggling' },
      { id: '40', name: 'Alex Johnson', sessions: 2, averageSessionLength: '11 min', lastSession: formatDate(80), status: 'Struggling' },
      { id: '41', name: 'Maya Thompson', sessions: 1, averageSessionLength: '9 min', lastSession: formatDate(85), status: 'Struggling' },
      { id: '42', name: 'Daniel Brown', sessions: 2, averageSessionLength: '7 min', lastSession: formatDate(90), status: 'Struggling' },
      { id: '43', name: 'Sophie Martin', sessions: 1, averageSessionLength: '6 min', lastSession: formatDate(95), status: 'Struggling' },
      { id: '44', name: 'Lucas Davis', sessions: 2, averageSessionLength: '10 min', lastSession: formatDate(100), status: 'Struggling' },
      { id: '45', name: 'Emma Wilson', sessions: 1, averageSessionLength: '8 min', lastSession: formatDate(105), status: 'Struggling' },
      
      // Inactive users (5 total)
      { id: '46', name: 'John Smith', sessions: 0, averageSessionLength: '0 min', lastSession: 'Never', status: 'Inactive' },
      { id: '47', name: 'Jane Doe', sessions: 0, averageSessionLength: '0 min', lastSession: 'Never', status: 'Inactive' },
      { id: '48', name: 'Tom Anderson', sessions: 1, averageSessionLength: '3 min', lastSession: formatDate(180), status: 'Inactive' },
      { id: '49', name: 'Lisa White', sessions: 1, averageSessionLength: '2 min', lastSession: formatDate(200), status: 'Inactive' },
      { id: '50', name: 'Mark Johnson', sessions: 0, averageSessionLength: '0 min', lastSession: 'Never', status: 'Inactive' },
    ];
  }

  public getCoachingThemes(): CoachingTheme[] {
    return [
      { id: '1', title: 'Staying Centered When Things Get Tense', sessions: 321 },
      { id: '2', title: 'Speaking with Clarity and Courage', sessions: 230 },
      { id: '3', title: 'Understanding and Expressing Emotions Constructively', sessions: 201 },
      { id: '4', title: 'Breaking Free from Limiting Beliefs', sessions: 200 },
      { id: '5', title: 'Setting Boundaries with Care', sessions: 180 },
      { id: '6', title: 'Building Inner Safety and Trust', sessions: 177 },
      { id: '7', title: 'Releasing the Pressure to Please', sessions: 150 },
    ];
  }

  public getUsersByStatus(status: string): AdminUser[] {
    const users = this.getUsers();
    if (status === 'all') return users;
    return users.filter(user => user.status.toLowerCase() === status.toLowerCase());
  }

  public getActiveUsers(): AdminUser[] {
    const users = this.getUsers();
    // Active users are those who have had sessions in the last 30 days
    // and are not in 'Inactive' status
    return users.filter(user => {
      if (user.status === 'Inactive') return false;
      
      // Parse last session date
      if (user.lastSession === 'Never') return false;
      
      const lastSessionDate = new Date(user.lastSession);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      return lastSessionDate >= thirtyDaysAgo;
    });
  }

  public getRepeatUsers(): AdminUser[] {
    const users = this.getUsers();
    // Repeat users are those who have had more than 3 sessions
    return users.filter(user => user.sessions > 3);
  }
}